import { useEffect } from 'react';

/**
 * [CF 2026-08-17 #5] Open every page at the top, forward AND on back/forward.
 *
 * This logic used to be copy-pasted into Routes.js and Routes2.js. They had
 * already drifted apart in other ways, so it lives here once and both call it.
 *
 * Why it is more than a single scrollTo:
 *
 * The app is a single page, so navigating keeps the previous page's scroll
 * offset — a detail page opened part-way down and the banner was above the fold.
 * `scrollRestoration = 'manual'` stops the browser re-applying its own remembered
 * offset on back/forward.
 *
 * One scrollTo on the route change is still not enough on iOS Safari. At that
 * moment the new page is empty, the document is one viewport tall and there is
 * nothing to scroll; when the rows and images arrive the document grows again
 * and Safari re-applies the offset it remembered. The fix re-asserts the top
 * while the page is settling.
 *
 * [CF 2026-08-17] Two changes after the client re-reported this:
 *   - the assert window ran for 1s, which is fine on a desk but not on a phone
 *     connection where images land later. Extended to 2.5s.
 *   - fixed timers alone are guesswork, so a ResizeObserver now re-asserts the
 *     moment the document actually grows, which is the event that triggers
 *     Safari's restore in the first place.
 *
 * It always yields to the user: the first touch or wheel cancels everything, so
 * this can never fight someone who has started scrolling deliberately.
 */
export default function useScrollTopOnRoute(pathname) {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let cancelled = false;
    const stop = () => {
      cancelled = true;
    };
    const toTop = () => {
      if (!cancelled && window.scrollY !== 0) window.scrollTo(0, 0);
    };

    window.scrollTo(0, 0);
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('wheel', stop, { passive: true });

    const timers = [0, 60, 150, 300, 600, 1000, 1600, 2500].map((d) =>
      setTimeout(toTop, d)
    );

    // Re-assert when the document grows — that is the moment Safari restores.
    let observer;
    if (typeof ResizeObserver !== 'undefined' && document.body) {
      observer = new ResizeObserver(toTop);
      observer.observe(document.body);
    }
    const release = setTimeout(() => observer && observer.disconnect(), 2600);

    return () => {
      stop();
      timers.forEach(clearTimeout);
      clearTimeout(release);
      if (observer) observer.disconnect();
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('wheel', stop);
    };
  }, [pathname]);
}
